import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { CVBuilderFormValues } from 'utils/cv-form-db';
import { PROGRESS_CONFIG } from '../constants';
import { getSectionStates, SectionState } from '../sectionState';
import { PROGRESS_STEPS, StepKey } from '../stepMeta';

export interface StepProgress {
  stepKey: StepKey;
  /** Locale key for the step name — resolve with `getTrans` at the render site. */
  labelKey: string;
  isComplete: boolean;
  weight: number;
  /** Full per-section detail (status, entry count, required/blocking) for richer UI. */
  section: SectionState;
}

/**
 * Wizard progress over the 7 progress-contributing steps, derived from the FORM DATA via
 * `sectionState` — no longer from whether the step was visited.
 *
 * That change is what makes the number honest for a returning talent: their CV loads from
 * the server with an empty `completedSteps` (it is per-grant for guests, and never sent at
 * all on the authenticated path), which used to read as 0/7 and forced the Summary to hide
 * the progress card behind a `hasSubmittedCv` special case.
 *
 * The denominator is unchanged — the same 7 steps pro-core counts in
 * `CvBuilderStep.countsTowardProgress` — and `values.completedSteps` is still sent to the
 * server untouched, so the operator funnel's percentage is unaffected.
 */
export const useProgressCalculation = () => {
  const formikContext = useFormikContext<CVBuilderFormValues>();
  const values = formikContext?.values;

  // Memoize expensive calculations to prevent running on every render.
  // Must be called unconditionally to follow Rules of Hooks.
  const progressData = useMemo(() => {
    // Handle missing context defensively
    if (!formikContext || !values) {
      console.warn('useProgressCalculation: Formik context not available');
      return {
        stepProgress: [],
        sections: [],
        totalProgress: 0,
        completedSteps: 0,
        totalSteps: 0,
        nextIncompleteStep: undefined,
        blockingSections: [],
        isComplete: false
      };
    }

    const sections = getSectionStates(values);
    const stepProgress: StepProgress[] = sections.map(section => {
      const meta = PROGRESS_STEPS.find(step => step.key === section.key);
      return {
        stepKey: section.key,
        labelKey: meta?.labelKey ?? '',
        isComplete: section.status === 'complete',
        weight: PROGRESS_CONFIG.STEP_WEIGHTS[section.key],
        section
      };
    });

    const totalProgress = stepProgress.reduce(
      (total, step) => total + (step.isComplete ? step.weight : 0),
      0
    );

    const completedSteps = stepProgress.filter(step => step.isComplete).length;

    return {
      stepProgress,
      sections,
      totalProgress,
      completedSteps,
      totalSteps: stepProgress.length,
      nextIncompleteStep: stepProgress.find(step => !step.isComplete),
      blockingSections: sections.filter(section => section.blocking),
      // Use a small epsilon to account for floating-point arithmetic errors
      // when summing weights like 100/7 (repeating decimal)
      isComplete: Math.abs(totalProgress - 100) < Number.EPSILON * 100
    };
  }, [formikContext, values]);

  return progressData;
};
